import Vue from 'vue'
import VueRouter from 'vue-router'
import { MessageBox } from 'element-ui'
import store from '@/store'
import { confirmLeaveIfFileUploading } from '@/utils/uploadLeaveConfirm'
import { applySiteBrand, loadSiteBrand } from '@/utils/siteBrand'
import { singleTabTitle, hasTabPermission } from '@/utils/tabPages'
import { resolveAnalysisRedirectPath } from '@/utils/analysisMenu'
import { isEnterpriseViewOrg } from '@/utils/bidLevelActions'

Vue.use(VueRouter)

function analysisOrgRedirect(pageName) {
  return () => {
    const orgUid = store.state.user && store.state.user.orgValue
    if (!orgUid) return '/home'
    return '/analysis/' + pageName + '/' + orgUid
  }
}

function analysisEntryRedirect() {
  return () => {
    const orgUid = store.state.user && store.state.user.orgValue
    if (!orgUid) return '/home'
    const pagesGetter = store.getters && store.getters['user/pages']
    const menuGetter = store.getters && store.getters['user/menuPages']
    const pages =
      Array.isArray(pagesGetter) && pagesGetter.length
        ? pagesGetter
        : Array.isArray(menuGetter) && menuGetter.length
          ? menuGetter
          : []
    const orgType = store.getters && store.getters['user/orgType']
    return resolveAnalysisRedirectPath(pages, orgUid, orgType)
  }
}

const routes = [
  { path: '/', redirect: '/login' },
  {
    path: '/login',
    component: () =>
      import(/* webpackChunkName: 'LoginView' */ '@/pages/LoginView.vue'),
    name: 'login'
  },
  {
    path: '/home',
    name: 'home',
    component: () =>
      import(/* webpackChunkName: 'HomeView' */ '@/pages/HomeView.vue'),
    children: [
      {
        path: '/analysis',
        redirect: analysisEntryRedirect(),
        name: 'analysis',
        meta: {
          icon: 'iconfont icon-shuju',
          title: '数据分析'
        }
      },
      {
        path: '/analysis/proRealnameData',
        redirect: analysisOrgRedirect('proRealnameData')
      },
      {
        path: '/analysis/proRealnameData/:orgUid',
        component: () =>
          import(
            /* webpackChunkName: 'DataAnalysis' */ '@/pages/DataAnalysis/DataAnalysis.vue'
          ),
        name: 'proRealnameData',
        meta: {
          icon: 'iconfont icon-category',
          title: '劳务用工'
        }
      },
      {
        path: '/analysis/officialCarData',
        redirect: analysisOrgRedirect('officialCarData')
      },
      {
        path: '/analysis/officialCarData/:orgUid',
        component: () =>
          import(
            /* webpackChunkName: 'DataAnalysis' */ '@/pages/DataAnalysis/DataAnalysis.vue'
          ),
        name: 'officialCarData',
        meta: {
          icon: 'iconfont icon-category',
          title: '智慧用车'
        }
      },
      {
        path: '/analysis/preClassCompanyData',
        redirect: analysisOrgRedirect('preClassCompanyData')
      },
      {
        path: '/analysis/preClassCompanyData/:orgUid',
        component: () =>
          import(
            /* webpackChunkName: 'PreClassAnalysis' */ '@/pages/DataAnalysis/PreClassAnalysis.vue'
          ),
        name: 'preClassCompanyData',
        meta: {
          icon: 'el-icon-reading',
          title: '班前教育'
        }
      },
      {
        path: '/analysis/preClassBidData',
        redirect: analysisOrgRedirect('preClassBidData')
      },
      {
        path: '/analysis/preClassBidData/:orgUid',
        component: () =>
          import(
            /* webpackChunkName: 'PreClassAnalysis' */ '@/pages/DataAnalysis/PreClassAnalysis.vue'
          ),
        name: 'preClassBidData',
        meta: {
          icon: 'el-icon-reading',
          title: '班前教育'
        }
      },
      {
        path: '/analysis/materialData',
        redirect: analysisOrgRedirect('materialData')
      },
      {
        path: '/analysis/materialData/:orgUid',
        component: () =>
          import(
            /* webpackChunkName: 'MaterialAnalysis' */ '@/pages/DataAnalysis/MaterialAnalysis.vue'
          ),
        name: 'materialData',
        meta: {
          icon: 'el-icon-box',
          title: '物资管理'
        }
      },
      {
        path: '/monitoring',
        component: () =>
          import(
            /* webpackChunkName: 'MonitoringPanel' */ '@/pages/Monitoring/MonitoringPanel.vue'
          ),
        name: 'monitoring',
        meta: {
          icon: 'iconfont icon-business_monitor',
          title: '监控中心'
        }
      },
      {
        path: '/todoTakas',
        component: () =>
          import(
            /* webpackChunkName: 'TodoTask' */ '@/pages/TodoTask/TodoTask.vue'
          ),
        name: 'todoTask',
        meta: {
          icon: 'iconfont icon-form-fill',
          title: '待办任务'
        }
      },
      {
        path: '/workbench',
        component: () =>
          import(
            /* webpackChunkName: 'WorkBench' */ '@/pages/WorkBench/WorkBench.vue'
          ),
        name: 'workbench',
        meta: {
          icon: 'iconfont icon-all-fill',
          title: '业务工作台'
        }
      },
      {
        path: '/workbench/personnelArchives',
        component: () =>
          import(
            /* webpackChunkName: 'PersonInfo' */ '@/pages/WorkBench/PersonnelArchives.vue'
          ),
        name: 'personnelArchives',
        meta: {
          icon: 'iconfont icon-order-fill',
          title: '个人档案'
        }
      },
      {
        path: '/workbench/enterpriseArchive',
        component: () =>
          import(
            /* webpackChunkName: 'BuildingEnterprise' */ '@/pages/WorkBench/EnterpriseArchive.vue'
          ),
        name: 'enterpriseArchive',
        meta: {
          icon: 'iconfont icon-manage-order-fill',
          title: '企业档案'
        }
      },
      {
        path: '/workbench/projectArchive',
        component: () =>
          import(
            /* webpackChunkName: 'BidSection' */ '@/pages/WorkBench/ProjectArchive.vue'
          ),
        name: 'projectArchive',
        meta: {
          icon: 'el-icon-s-order',
          title: '项目档案'
        }
      },
      {
        path: '/workbench/contractor',
        component: () =>
          import(
            /* webpackChunkName: 'BuildingContractor' */ '@/pages/WorkBench/Contractor/BuildingContractor.vue'
          ),
        name: 'contractor',
        meta: {
          icon: 'iconfont icon-company-fill',
          title: '参建单位'
        }
      },
      {
        path: '/workbench/teamManagement',
        component: () =>
          import(
            /* webpackChunkName: 'TeamManagement' */ '@/pages/WorkBench/TeamManagement/TeamManagement.vue'
          ),
        name: 'teamManagement',
        meta: {
          icon: 'iconfont icon-Moneymanagement-fill',
          title: '班组管理'
        }
      },
      {
        path: '/workbench/builder',
        component: () =>
          import(
            /* webpackChunkName: 'BuildingPerson' */ '@/pages/WorkBench/Builder/BuildingPerson.vue'
          ),
        name: 'builder',
        meta: {
          icon: 'el-icon-s-custom',
          title: '参建人员'
        }
      },
      {
        path: '/workbench/esign',
        component: () =>
          import(
            /* webpackChunkName: 'SignedCompose' */ '@/pages/WorkBench/SignedCompose.vue'
          ),
        name: 'esign',
        meta: {
          title: '电子签署'
        }
      },
      {
        path: '/workbench/contract',
        component: () =>
          import(
            /* webpackChunkName: 'ContractManage' */ '@/pages/WorkBench/Contract/ContractManage.vue'
          ),
        name: 'contract',
        meta: {
          icon: 'iconfont icon-hetong',
          title: '合同管理'
        }
      },
      {
        path: '/workbench/attendanceStatistics',
        component: () =>
          import(
            /* webpackChunkName: 'AttendanceStatistics' */ '@/pages/WorkBench/AttendanceStatistics/AttendanceStatistics'
          ),
        name: 'attendanceStatistics',
        meta: {
          icon: 'iconfont icon-kaoqintongji',
          title: '考勤统计'
        }
      },
      {
        path: '/workbench/salaryPayment',
        component: () =>
          import(
            /* webpackChunkName: 'SalaryPayment' */ '@/pages/WorkBench/SalaryPayment/SalaryPayment'
          ),
        name: 'salaryPayment',
        meta: {
          icon: 'iconfont icon-licaitouzi',
          title: '薪酬统计'
        }
      },
      {
        path: '/workbench/division',
        component: () =>
          import(
            /* webpackChunkName: 'EngineeringDivision' */ '@/pages/WorkBench/Division/EngineeringDivision'
          ),
        name: 'division',
        meta: {
          icon: 'iconfont icon-product',
          title: '工程划分'
        }
      },
      {
        path: '/workbench/progress',
        component: () =>
          import(
            /* webpackChunkName: 'EngineeringProgress' */ '@/pages/WorkBench/Progress/EngineeringProgress'
          ),
        name: 'progress',
        meta: {
          icon: 'iconfont icon-jindutiao',
          title: '工程进度'
        }
      },
      {
        path: '/workbench/billOfQuantities',
        component: () =>
          import(
            /* webpackChunkName: 'BillOfQuantities' */ '@/pages/WorkBench/BillOfQuantities/BillOfQuantities'
          ),
        name: 'billOfQuantities',
        meta: {
          icon: 'el-icon-document',
          title: '工程量清单'
        }
      },
      {
        path: '/workbench/waterwheelWork',
        component: () =>
          import(
            /* webpackChunkName: 'WaterwheelWork' */ '@/pages/WorkBench/WaterwheelWork/WaterwheelWork'
          ),
        name: 'waterwheelWork',
        meta: {
          icon: 'iconfont icon-sashuiche',
          title: '水车作业'
        }
      },
      {
        path: '/workbench/training',
        component: () =>
          import(
            /* webpackChunkName: 'TrainingManage' */ '@/pages/WorkBench/Training/TrainingManage'
          ),
        name: 'training',
        meta: {
          icon: 'el-icon-microphone',
          title: '培训教育'
        }
      },
      {
        path: '/workbench/preClassEducation',
        component: () =>
          import(
            /* webpackChunkName: 'PreClassEducation' */ '@/pages/WorkBench/Training/PreClassEducation'
          ),
        name: 'preClassEducation',
        meta: {
          icon: 'el-icon-reading',
          title: '班前教育'
        }
      },
      {
        path: '/workbench/trainingMaterials',
        component: () =>
          import(
            /* webpackChunkName: 'TrainingMaterials' */ '@/pages/WorkBench/Training/TrainingMaterials'
          ),
        name: 'trainingMaterials',
        meta: {
          icon: 'el-icon-folder-opened',
          title: '培训资料'
        }
      },
      {
        path: '/workbench/publicMaterials',
        component: () =>
          import(
            /* webpackChunkName: 'PublicMaterials' */ '@/pages/WorkBench/Training/PublicMaterials'
          ),
        name: 'publicMaterials',
        meta: {
          icon: 'el-icon-folder-opened',
          title: '公共资料库'
        }
      },
      {
        path: '/workbench/materialArticle/:uid',
        component: () =>
          import(
            /* webpackChunkName: 'MaterialArticle' */ '@/pages/WorkBench/Training/MaterialArticle'
          ),
        name: 'materialArticle',
        meta: {
          icon: 'el-icon-document',
          title: '图文详情'
        }
      },
      {
        path: '/workbench/materialLibrary',
        component: () =>
          import(
            /* webpackChunkName: 'MaterialLibrary' */ '@/pages/WorkBench/Training/MaterialLibrary'
          ),
        name: 'materialLibrary',
        meta: {
          icon: 'el-icon-notebook-2',
          title: '资料库'
        }
      },
      {
        path: '/workbench/paperLibrary',
        component: () =>
          import(
            /* webpackChunkName: 'PaperLibrary' */ '@/pages/WorkBench/Training/PaperLibrary'
          ),
        name: 'paperLibrary',
        meta: {
          icon: 'el-icon-document',
          title: '试卷库'
        }
      },
      {
        path: '/workbench/questionLibrary',
        component: () =>
          import(
            /* webpackChunkName: 'QuestionLibrary' */ '@/pages/WorkBench/Training/QuestionLibrary'
          ),
        name: 'questionLibrary',
        meta: {
          icon: 'el-icon-collection',
          title: '试题库'
        }
      },
      {
        path: '/workbench/publicPapers',
        component: () =>
          import(
            /* webpackChunkName: 'PublicPapers' */ '@/pages/WorkBench/Training/PublicPapers'
          ),
        name: 'publicPapers',
        meta: {
          icon: 'el-icon-document',
          title: '公共试卷库'
        }
      },
      {
        path: '/workbench/publicQuestions',
        component: () =>
          import(
            /* webpackChunkName: 'PublicQuestions' */ '@/pages/WorkBench/Training/PublicQuestions'
          ),
        name: 'publicQuestions',
        meta: {
          icon: 'el-icon-collection',
          title: '公共试题库'
        }
      },
      {
        path: '/workbench/questionImport',
        component: () =>
          import(
            /* webpackChunkName: 'QuestionImport' */ '@/pages/WorkBench/Training/QuestionImport'
          ),
        name: 'questionImport',
        meta: {
          icon: 'el-icon-upload',
          title: '导入题目'
        }
      },
      {
        path: '/workbench/onlineExam',
        component: () =>
          import(
            /* webpackChunkName: 'OnlineExam' */ '@/pages/WorkBench/Training/OnlineExam'
          ),
        name: 'onlineExam',
        meta: {
          icon: 'el-icon-link',
          title: '在线考试'
        }
      },
      {
        path: '/workbench/safetyInput',
        component: () =>
          import(
            /* webpackChunkName: 'SafetyInput' */ '@/pages/WorkBench/SafetyInput/SafetyInput'
          ),
        name: 'safetyInput',
        meta: {
          icon: 'iconfont icon-fengxian',
          title: '安全投入'
        }
      },
      {
        path: '/workbench/materialManage',
        component: () =>
          import(
            /* webpackChunkName: 'MaterialManage' */ '@/pages/WorkBench/MaterialManage/MaterialManage'
          ),
        name: 'materialManage',
        meta: {
          icon: 'el-icon-box',
          title: '物资管理'
        }
      },
      {
        path: '/workbench/officialCar',
        component: () =>
          import(
            /* webpackChunkName: 'OfficialCar' */ '@/pages/WorkBench/OfficialCar/OfficialCar'
          ),
        name: 'officialCar',
        meta: {
          icon: 'iconfont icon-wajueji',
          title: '施工机械'
        }
      },
      {
        path: '/workbench/ownEquipment',
        component: () =>
          import(
            /* webpackChunkName: 'OwnEquipment' */ '@/pages/WorkBench/OwnEquipment/OwnEquipment'
          ),
        name: 'ownEquipment',
        meta: {
          icon: 'iconfont icon-wajueji',
          title: '自有机械'
        }
      },
      {
        path: '/workbench/concealedWork',
        component: () =>
          import(
            /* webpackChunkName: 'ConcealedWork' */ '@/pages/WorkBench/ConcealedWork/ConcealedWork'
          ),
        name: 'concealedWork',
        meta: {
          icon: 'el-icon-view',
          title: '隐蔽工程'
        }
      },
      {
        path: '/workbench/pouringOrder',
        component: () =>
          import(
            /* webpackChunkName: 'pouringOrder' */ '@/pages/WorkBench/PouringOrder/PouringOrder'
          ),
        name: 'pouringOrder',
        meta: {
          icon: 'iconfont icon-product',
          title: '砼浇筑令'
        }
      },
      {
        path: '/workbench/goodsShipment',
        component: () =>
          import(
            /* webpackChunkName: 'goodsShipment' */ '@/pages/WorkBench/GoodsShipment/GoodsShipment'
          ),
        name: 'goodsShipment',
        meta: {
          icon: 'el-icon-truck',
          title: '物料运输'
        }
      },
      {
        path: '/workbench/mixingProduction',
        component: () =>
          import(
            /* webpackChunkName: 'mixingProduction' */ '@/pages/WorkBench/MixingProduction/MixingProduction'
          ),
        name: 'mixingProduction',
        meta: {
          icon: 'iconfont icon-gongchang',
          title: '砼拌站生产'
        }
      },
      {
        path: '/workbench/department',
        component: () =>
          import(
            /* webpackChunkName: 'DepartmentManage' */ '@/pages/WorkBench/Department/DepartmentManage'
          ),
        name: 'department',
        meta: {
          icon: 'iconfont icon-w_bailing',
          title: '职能部门'
        }
      },
      {
        path: '/workbench/factory',
        component: () =>
          import(
            /* webpackChunkName: 'FactoryManage' */ '@/pages/WorkBench/Factory/FactoryManage'
          ),
        name: 'factory',
        meta: {
          icon: 'iconfont icon-fuwuqu',
          title: '场站工区'
        }
      },
      {
        path: '/workbench/engineeringModel',
        component: () =>
          import(
            /* webpackChunkName: 'EngineeringModel' */ '@/pages/WorkBench/EngineeringModel/EngineeringModel'
          ),
        name: 'engineeringModel',
        meta: {
          icon: 'iconfont icon-gongluqiaoliang',
          title: '模型库'
        }
      },
      {
        path: '/workbench/attendanceSetting',
        component: () =>
          import(
            /* webpackChunkName: 'AttendanceSetting' */ '@/pages/SystemSettings/AttendanceSetting/AttendanceSetting'
          ),
        name: 'attendanceSetting',
        meta: {
          icon: 'el-icon-time',
          title: '考勤设置'
        }
      },
      {
        path: '/workbench/notice',
        component: () =>
          import(
            /* webpackChunkName: 'NoticeAnnouncement' */ '@/pages/WorkBench/Notice/NoticeAnnouncement'
          ),
        name: 'notice',
        meta: {
          icon: 'iconfont icon-notice-fill',
          title: '通知公告'
        }
      },
      {
        path: '/workbench/manageEvaluation',
        component: () =>
          import(
            /* webpackChunkName: 'ManageEvaluation' */ '@/pages/WorkBench/ManageEvaluation/ManageEvaluation'
          ),
        name: 'manageEvaluation',
        meta: {
          icon: 'el-icon-star-off',
          title: '管理考评'
        }
      },
      {
        path: '/workbench/buildProject',
        component: () =>
          import(
            /* webpackChunkName: 'EngineeringProject' */ '@/pages/Project/EngineeringProject.vue'
          ),
        name: 'buildProject',
        meta: {
          icon: 'iconfont icon-integral-fill',
          title: '建设项目'
        }
      },
      {
        path: '/workbench/projectSection',
        component: () =>
          import(
            /* webpackChunkName: 'BidSection' */ '@/pages/BidSection/BidSection.vue'
          ),
        name: 'projectSection',
        meta: {
          icon: 'iconfont icon-form-fill',
          title: '项目标段'
        }
      },
      {
        path: '/project',
        component: () =>
          import(
            /* webpackChunkName: 'EngineeringProject' */ '@/pages/Project/EngineeringProject.vue'
          ),
        name: 'project',
        meta: {
          icon: 'iconfont icon-integral-fill',
          title: '工程项目'
        }
      },
      {
        path: '/enterprise',
        component: () =>
          import(
            /* webpackChunkName: 'BuildingEnterprise' */ '@/pages/Enterprise/BuildingEnterprise.vue'
          ),
        name: 'enterprise',
        meta: {
          icon: 'iconfont icon-company-fill',
          title: '企业信息'
        }
      },
      {
        path: '/bidSection',
        component: () =>
          import(
            /* webpackChunkName: 'BidSection' */ '@/pages/BidSection/BidSection.vue'
          ),
        name: 'bidSection',
        meta: {
          icon: 'iconfont icon-form-fill',
          title: '项目标段'
        }
      },
      {
        path: '/personnel',
        component: () =>
          import(
            /* webpackChunkName: 'PersonInfo' */ '@/pages/Personnel/PersonInfo.vue'
          ),
        name: 'personnel',
        meta: {
          icon: 'iconfont icon-online-tracking-fill',
          title: '人员信息'
        }
      },
      {
        path: '/mechanicalEquipment',
        component: () =>
          import(
            /* webpackChunkName: 'MechanicalEquipment' */ '@/pages/MechanicalEquipment/MechanicalEquipment.vue'
          ),
        name: 'mechanicalEquipment',
        meta: {
          icon: 'iconfont icon-wajueji',
          title: '施工机械'
        }
      },
      {
        path: '/engineeringModel',
        component: () =>
          import(
            /* webpackChunkName: 'SystemEngineeringModel' */ '@/pages/SystemSettings/EngineeringModel/SystemEngineeringModel'
          ),
        name: 'systemEngineeringModel',
        meta: {
          icon: 'iconfont icon-gongluqiaoliang',
          title: '模型库'
        }
      },
      {
        path: '/dataReport',
        redirect: '/dataReport/ProRealnameReport',
        name: 'dataReport',
        meta: {
          icon: 'iconfont icon-order-fill',
          title: '数据报表'
        }
      },
      {
        path: '/dataReport/ProRealnameReport',
        component: () =>
          import(
            /* webpackChunkName: 'ProRealnameReport' */ '@/pages/DataReport/ProRealnameReport'
          ),
        name: 'proRealnameReport',
        meta: {
          icon: 'iconfont icon-order-fill',
          title: '劳务用工'
        }
      },
      {
        path: '/dataReport/preClassEduReport',
        component: () =>
          import(
            /* webpackChunkName: 'ProRealnameReport' */ '@/pages/DataReport/ProRealnameReport'
          ),
        name: 'preClassEduReport',
        meta: {
          icon: 'iconfont icon-order-fill',
          title: '班前教育'
        }
      },
      {
        path: '/dataReport/:reportKey',
        component: () =>
          import(
            /* webpackChunkName: 'ProRealnameReport' */ '@/pages/DataReport/ProRealnameReport'
          ),
        name: 'orgReportPage',
        meta: {
          icon: 'iconfont icon-order-fill',
          title: '数据报表'
        }
      },
      {
        path: '/userRights',
        redirect: '/userRights/user',
        name: 'userRights'
      },
      {
        path: '/userRights/user',
        component: () =>
          import(
            /* webpackChunkName: 'UserManage' */ '@/pages/UserRights/User/UserManage'
          ),
        name: 'user',
        meta: {
          icon: 'iconfont icon-category',
          title: '用户管理'
        }
      },
      {
        path: '/userRights/rights',
        component: () =>
          import(
            /* webpackChunkName: 'RightsManage' */ '@/pages/UserRights/Rights/RightsManage'
          ),
        name: 'rights',
        meta: {
          icon: 'iconfont icon-category',
          title: '权限管理'
        }
      },
      {
        path: '/systemSettings',
        redirect: '/systemSettings/organization',
        name: 'systemSettings',
        meta: {
          icon: 'iconfont icon-set',
          title: '系统设置'
        }
      },
      {
        path: '/systemSettings/organization',
        component: () =>
          import(
            /* webpackChunkName: 'OrganManage' */ '@/pages/SystemSettings/Organization/OrganManage'
          ),
        name: 'organization',
        meta: {
          icon: 'iconfont icon-category',
          title: '组织结构'
        }
      },
      {
        path: '/systemSettings/systemFields',
        component: () =>
          import(
            /* webpackChunkName: 'SystemFields' */ '@/pages/SystemSettings/SystemFields/SystemFields'
          ),
        name: 'systemFields',
        meta: {
          icon: 'iconfont icon-category',
          title: '系统字段'
        }
      },
      {
        path: '/systemSettings/engineeringModel',
        redirect: '/engineeringModel'
      },
      {
        path: '/systemSettings/systemPage',
        component: () =>
          import(
            /* webpackChunkName: 'SystemPage' */ '@/pages/SystemSettings/SystemPage/SystemPage'
          ),
        name: 'systemPage',
        meta: {
          icon: 'iconfont icon-category',
          title: '系统页面'
        }
      },
      {
        path: '/systemSettings/attendanceSetting',
        redirect: '/workbench/attendanceSetting'
      },
      {
        path: '/systemSettings/domainBrand',
        component: () =>
          import(
            /* webpackChunkName: 'DomainBrand' */ '@/pages/SystemSettings/DomainBrand/DomainBrand'
          ),
        name: 'domainBrand',
        meta: {
          icon: 'el-icon-link',
          title: '域名配置'
        }
      },
      {
        path: '/systemSettings/ruleConfig',
        component: () =>
          import(
            /* webpackChunkName: 'RuleConfig' */ '@/pages/SystemSettings/RuleConfig'
          ),
        name: 'systemRuleConfig',
        meta: {
          icon: 'el-icon-set-up',
          title: '规则配置'
        }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// 挂载路由导航守卫
router.beforeEach(async (to, from, next) => {
  await loadSiteBrand()
  applySiteBrand()
  // to and from are both route objects. must call `next`.
  const tokenStr = window.sessionStorage.getItem('token')
  if (to.path === '/login') {
    if (
      tokenStr &&
      !(await confirmLeaveIfFileUploading(MessageBox.confirm, store))
    ) {
      return next(false)
    }
    return next()
  }
  if (!tokenStr) return next('/login')
  if (
    !store.getters['user/isPlatformOrg'] &&
    (to.path === '/systemSettings/organization' ||
      to.path === '/engineeringModel')
  ) {
    return next('/home')
  }
  if (to.path === '/systemSettings/domainBrand') {
    const isSuperAdmin =
      store.state.user.userName === store.state.superAdminName ||
      store.state.user.personName === store.state.superAdminName
    if (!isSuperAdmin || !store.getters['user/isPlatformOrg']) {
      return next('/home')
    }
  }
  if (to.path === '/systemSettings/ruleConfig') {
    const isSuperAdmin =
      store.state.user.userName === store.state.superAdminName ||
      store.state.user.personName === store.state.superAdminName
    if (!isSuperAdmin) {
      return next('/home')
    }
  }
  // 公共库入口：需对应页签权限（超管放行）
  {
    const isSuperAdmin =
      store.state.user.userName === store.state.superAdminName ||
      store.state.user.personName === store.state.superAdminName
    if (!isSuperAdmin) {
      const pages = store.getters['user/pages']
      const isEnterprise = isEnterpriseViewOrg(store.getters['user/orgType'])
      if (
        to.path === '/workbench/publicMaterials' &&
        !isEnterprise &&
        !hasTabPermission(pages, 'trainingMaterials', '公共资料库')
      ) {
        return next('/workbench/trainingMaterials')
      }
      if (
        to.path === '/workbench/publicPapers' &&
        !hasTabPermission(pages, 'onlineExam', '公共试卷库')
      ) {
        return next('/workbench/onlineExam')
      }
      if (
        to.path === '/workbench/publicQuestions' &&
        !hasTabPermission(pages, 'onlineExam', '公共试题库')
      ) {
        return next('/workbench/onlineExam')
      }
    }
  }
  const routeTitle = to.meta && to.meta.title ? to.meta.title : ''
  const tabTitle = singleTabTitle(store.getters['user/pages'], to.name)
  const pageTitle = tabTitle || routeTitle
  if (
    store.state.activePath !== to.path &&
    !(await confirmLeaveIfFileUploading(MessageBox.confirm, store))
  ) {
    return next(false)
  }
  // 路径相同也要写标题：登录后菜单会先 commit activePath 再 push，
  // 以及切机构时 watcher 抢先改 activePath，旧逻辑会跳过导致顶栏空白或不更新
  if (pageTitle) {
    store.commit('baseSet', {
      key: 'pageTitle',
      value: pageTitle
    })
  }
  if (store.state.activePath === to.path) return next()
  store.commit('baseSet', { key: 'dialogFormVisible', value: false })
  store.commit('baseSet', { key: 'dialogTableVisible', value: false })
  store.commit('baseSet', { key: 'drawerTableVisible', value: false })
  store.commit('baseSet', { key: 'imageVisible', value: false })
  store.commit('baseSet', { key: 'currentStep', value: {} })
  store.commit('baseSet', {
    key: 'activePath',
    value: to.path
  })
  store.commit('baseSet', {
    key: 'pageTitle',
    value: pageTitle
  })
  /* store.commit('baseSet', {
    key: 'pageTitle',
    value: to.name !== 'officialCar' && to.meta ? to.meta.title : '特种设备检验'
  }) */
  store.commit('baseSet', {
    key: 'currentTab',
    value: { label: '', theme: '', checkFun: '' }
  })
  const breadcrumbKeep = store.state.routeBreadcrumbKeep
  store.commit('baseSet', { key: 'routeBreadcrumbKeep', value: null })
  store.commit('baseSet', {
    key: 'historyStep',
    value: Array.isArray(breadcrumbKeep) ? breadcrumbKeep : []
  })
  applySiteBrand()
  next()
})

export default router
